import { useState } from "react";
import { Table, TableHead, TableRow, TableCell, TableBody, useTheme } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { Box, Text } from "@/ui";
import { ReportFacture } from "@/types/nurse";
import { formatUTC } from "@/utils/format";
import { FactureSortKey, SortDirection, sortFactures } from "@/utils/report";

const columns: { key: FactureSortKey; label: string; width: number; align?: "center"; wrapHeader?: boolean }[] = [
  { key: "libelle", label: "Données", width: 105 },
  { key: "numero", label: "Facture", width: 85 },
  { key: "patient", label: "Patient", width: 115 },
  { key: "debut", label: "Début", width: 70 },
  { key: "fin", label: "Fin", width: 65 },
  { key: "rc", label: "Montant RC", width: 75, align: "center", wrapHeader: false },
  { key: "ro", label: "Montant RO", width: 75, align: "center", wrapHeader: false },
  { key: "montant", label: "Montant Total", width: 80, align: "center", wrapHeader: false },
];

const wrapCellStyle: React.CSSProperties = { whiteSpace: "normal", wordBreak: "break-word" };
// Réserve la hauteur de 2 lignes même quand le texte tient sur une seule, pour une grille de lignes homogène.
const twoLineTextStyle: React.CSSProperties = { ...wrapCellStyle, lineHeight: "14px", minHeight: 28 };

// Conteneur de scroll dédié : une largeur explicite (100%) évite le piège du
// centrage flex qui rend le début d'un contenu débordant inatteignable au scroll.
export function FactureTable({ factures }: { factures: ReportFacture[] }) {
  const theme = useTheme();
  const [sortKey, setSortKey] = useState<FactureSortKey | null>(null);
  const [direction, setDirection] = useState<SortDirection>("asc");

  const handleSort = (key: FactureSortKey) => {
    if (sortKey === key) {
      setDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setDirection("asc");
    }
  };

  const sortedFactures = sortFactures(factures || [], sortKey, direction);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <Table size="small" style={{ border: "1px solid #e0e0e0", tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const isActive = sortKey === column.key;
              return (
                <TableCell
                  key={column.key}
                  align={column.align}
                  onClick={() => handleSort(column.key)}
                  style={{
                    width: column.width,
                    padding: "8px 10px",
                    cursor: "pointer",
                    userSelect: "none",
                    ...(column.wrapHeader ? wrapCellStyle : { whiteSpace: "nowrap" }),
                  }}
                >
                  <Box flexDirection="row" alignItems="center" justifyContent={column.align === "center" ? "center" : "flex-start"}>
                    <span>{column.label}</span>
                    {isActive &&
                      (direction === "asc" ? (
                        <ArrowDownwardIcon style={{ fontSize: 14, marginLeft: 4, color: theme.palette.primary.main }} />
                      ) : (
                        <ArrowUpwardIcon style={{ fontSize: 14, marginLeft: 4, color: theme.palette.primary.main }} />
                      ))}
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedFactures.map((facture, detailIndex) => (
            <TableRow key={`${facture?.numero}-${detailIndex}`} style={{ backgroundColor: detailIndex % 2 === 0 ? "#fff" : "#f3e7e765" }}>
              <TableCell style={{ width: columns[0].width, padding: "8px 10px", verticalAlign: "top" }}>
                <Text fontWeight="700" fontSize={11} style={twoLineTextStyle}>
                  {facture?.libelle}
                </Text>
              </TableCell>
              <TableCell style={{ width: columns[1].width, padding: "8px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {facture?.numero}
              </TableCell>
              <TableCell style={{ width: columns[2].width, padding: "8px 10px", verticalAlign: "middle", ...twoLineTextStyle }}>{facture?.patient}</TableCell>
              <TableCell style={{ width: columns[3].width, padding: "8px 10px", whiteSpace: "nowrap" }}>
                {formatUTC(new Date(facture?.debut), "dd/MM/yy")}
              </TableCell>
              <TableCell style={{ width: columns[4].width, padding: "8px 10px", whiteSpace: "nowrap" }}>
                {formatUTC(new Date(facture?.fin), "dd/MM/yy")}
              </TableCell>
              <TableCell align="right" style={{ width: columns[5].width, padding: "8px 10px", whiteSpace: "nowrap" }}>
                {parseFloat(facture?.rc || "0").toFixed(2)} €
              </TableCell>
              <TableCell align="right" style={{ width: columns[6].width, padding: "8px 10px", whiteSpace: "nowrap" }}>
                {parseFloat(facture?.ro || "0").toFixed(2)} €
              </TableCell>
              <TableCell align="right" style={{ width: columns[7].width, padding: "8px 10px", whiteSpace: "nowrap" }}>
                {parseFloat(facture?.montant || "0").toFixed(2)} €
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
